import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { fg } from '@atlaskit/platform-feature-flags';
import { startLighthouseObserver } from '../additional-payload';
import { setUFOConfig } from '../config';
import { experimentalVC, sinkExperimentalHandler } from '../create-experimental-interaction-metrics-payload';
import { sinkExtraSearchPageInteractionHandler } from '../create-extra-search-page-interaction-payload';
import { setupHiddenTimingCapture } from '../hidden-timing';
import { interactionExtraMetrics, postInteractionLog, sinkInteractionHandler, sinkPostInteractionLogHandler } from '../interaction-metrics';
import { getPerformanceObserver } from '../interactions-performance-observer';
import { initialiseMemoryObserver, initialisePressureObserver } from '../machine-utilisation';
import scheduleIdleCallback from './schedule-idle-callback';
var initialized = false;
function sinkInteraction(instance, payloadPackage) {
  function sinkFn(interactionId, interaction) {
    function onIdle() {
      payloadPackage.createPayloads(interactionId, interaction).then(function (payloads) {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and Criterion
        var devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
        payloads === null || payloads === void 0 || payloads.forEach(function (payload) {
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 || devToolObserver(payload);
          }
          instance.sendOperationalEvent(payload);
        });
      }).catch(function (error) {
        throw error;
      });
    }
    scheduleIdleCallback(onIdle);
  }
  sinkInteractionHandler(sinkFn);
}
function sinkExperimentalInteractionMetrics(instance, payloadPackage) {
  function experimentalMetricsSinkFn(interactionId, interaction) {
    function experimentalMetricsOnIdle() {
      var payloadPromise = payloadPackage.createExperimentalMetricsPayload(interactionId, interaction);
      payloadPromise.then(function (payload) {
        if (payload) {
          if (fg('enable_ufo_devtools_api_for_extra_events')) {
            // NOTE: This API is used by the UFO DevTool Chrome Extension and Criterion
            var devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
            if (typeof devToolObserver === 'function') {
              devToolObserver === null || devToolObserver === void 0 || devToolObserver(payload);
            }
          }
          instance.sendOperationalEvent(payload);
        }
      });
    }
    scheduleIdleCallback(experimentalMetricsOnIdle);
  }
  sinkExperimentalHandler(experimentalMetricsSinkFn);
}
function sinkPostInteractionLog(instance, createPostInteractionLogPayload) {
  sinkPostInteractionLogHandler(function (logOutput) {
    scheduleIdleCallback(function () {
      var payload = createPostInteractionLogPayload(logOutput);
      if (payload) {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and also by Criterion
        if (fg('enable_ufo_devtools_api_for_extra_events')) {
          var devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 || devToolObserver(payload);
          }
        }
        instance.sendOperationalEvent(payload);
      }
    });
  });
}
function sinkInteractionExtraMetrics(instance, createInteractionExtraLogPayload) {
  interactionExtraMetrics.sinkHandler(function (interactionId, interaction, lastInteractionFinish, lastInteractionFinishVCResult) {
    scheduleIdleCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var payload, devToolObserver;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return createInteractionExtraLogPayload(interactionId, interaction, lastInteractionFinish, lastInteractionFinishVCResult);
          case 2:
            payload = _context.sent;
            if (payload) {
              // NOTE: This API is used by the UFO DevTool Chrome Extension and also by Criterion
              if (fg('enable_ufo_devtools_api_for_extra_events')) {
                devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
                if (typeof devToolObserver === 'function') {
                  devToolObserver === null || devToolObserver === void 0 || devToolObserver(payload);
                }
              }
              instance.sendOperationalEvent(payload);
            }
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    })));
  });
}
function sinkExtraSearchPageInteraction(instance, payloadPackage) {
  function sinkFn(interactionId, interaction) {
    function onIdle() {
      payloadPackage.createExtraSearchPageInteractionPayload(interactionId, interaction).then(function (payloads) {
        // NOTE: This API is used by the UFO DevTool Chrome Extension and Criterion
        var devToolObserver = globalThis.__ufo_devtool_onUfoPayload;
        payloads === null || payloads === void 0 || payloads.forEach(function (payload) {
          if (typeof devToolObserver === 'function') {
            devToolObserver === null || devToolObserver === void 0 || devToolObserver(payload);
          }
          instance.sendOperationalEvent(payload);
        });
      }).catch(function (error) {
        throw error;
      });
    }
    scheduleIdleCallback(onIdle);
  }
  sinkExtraSearchPageInteractionHandler(sinkFn);
}
export function init(analyticsWebClientAsync, config) {
  var _config$vc;
  if (initialized) {
    return;
  }

  // Initialize pressure observer for CPU usage monitoring
  initialisePressureObserver();
  initialiseMemoryObserver();
  setUFOConfig(config);
  if ((_config$vc = config.vc) !== null && _config$vc !== void 0 && _config$vc.enabled) {
    var _config$experimentalI, _config$extraInteract;
    var vcOptions = {
      heatmapSize: config.vc.heatmapSize,
      oldDomUpdates: config.vc.oldDomUpdates,
      devToolsEnabled: config.vc.devToolsEnabled,
      selectorConfig: config.vc.selectorConfig,
      ssrEnablePageLayoutPlaceholder: config.vc.ssrEnablePageLayoutPlaceholder
    };
    postInteractionLog.initializeVCObserver(vcOptions);
    if (config !== null && config !== void 0 && (_config$experimentalI = config.experimentalInteractionMetrics) !== null && _config$experimentalI !== void 0 && _config$experimentalI.enabled) {
      experimentalVC.initialize(vcOptions).start({
        startTime: 0
      });
    }
    if (config !== null && config !== void 0 && (_config$extraInteract = config.extraInteractionMetrics) !== null && _config$extraInteract !== void 0 && _config$extraInteract.enabled) {
      interactionExtraMetrics.initializeVCObserver(vcOptions);
    }
  }
  setupHiddenTimingCapture();
  startLighthouseObserver();
  initialized = true;
  if (typeof PerformanceObserver !== 'undefined') {
    var observer = getPerformanceObserver();
    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 16
    });
  }
  Promise.all([analyticsWebClientAsync, import( /* webpackChunkName: "create-payloads" */'../create-payload'), import( /* webpackChunkName: "create-post-interaction-log-payload" */'../create-post-interaction-log-payload'), import( /* webpackChunkName: "create-interaction-extra-metrics-payload" */'../create-interaction-extra-metrics-payload')]).then(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 4),
      awc = _ref3[0],
      payloadPackage = _ref3[1],
      createPostInteractionLogPayloadPackage = _ref3[2],
      createInteractionExtraMetricsPayloadPackage = _ref3[3];
    if (awc.getAnalyticsWebClientPromise) {
      awc.getAnalyticsWebClientPromise().then(function (client) {
        var _config$experimentalI2, _config$postInteracti, _config$extraInteract2, _config$extraSearchPa;
        var instance = client.getInstance();
        sinkInteraction(instance, payloadPackage);
        if (config !== null && config !== void 0 && (_config$experimentalI2 = config.experimentalInteractionMetrics) !== null && _config$experimentalI2 !== void 0 && _config$experimentalI2.enabled) {
          sinkExperimentalInteractionMetrics(instance, payloadPackage);
        }
        if ((_config$postInteracti = config.postInteractionLog) !== null && _config$postInteracti !== void 0 && _config$postInteracti.enabled) {
          sinkPostInteractionLog(instance, createPostInteractionLogPayloadPackage.default);
        }
        if (config !== null && config !== void 0 && (_config$extraInteract2 = config.extraInteractionMetrics) !== null && _config$extraInteract2 !== void 0 && _config$extraInteract2.enabled && fg('platform_ufo_enable_ttai_with_3p')) {
          sinkInteractionExtraMetrics(instance, createInteractionExtraMetricsPayloadPackage.default);
        }
        if (config !== null && config !== void 0 && (_config$extraSearchPa = config.extraSearchPageInteraction) !== null && _config$extraSearchPa !== void 0 && _config$extraSearchPa.enabled && fg('react_ufo_unified_search_ignoring_sain_metric')) {
          sinkExtraSearchPageInteraction(instance, payloadPackage);
        }
      });
    } else if (awc.sendOperationalEvent) {
      var _config$experimentalI3, _config$postInteracti2, _config$extraInteract3, _config$extraSearchPa2;
      sinkInteraction(awc, payloadPackage);
      if (config !== null && config !== void 0 && (_config$experimentalI3 = config.experimentalInteractionMetrics) !== null && _config$experimentalI3 !== void 0 && _config$experimentalI3.enabled) {
        sinkExperimentalInteractionMetrics(awc, payloadPackage);
      }
      if ((_config$postInteracti2 = config.postInteractionLog) !== null && _config$postInteracti2 !== void 0 && _config$postInteracti2.enabled) {
        sinkPostInteractionLog(awc, createPostInteractionLogPayloadPackage.default);
      }
      if (config !== null && config !== void 0 && (_config$extraInteract3 = config.extraInteractionMetrics) !== null && _config$extraInteract3 !== void 0 && _config$extraInteract3.enabled && fg('platform_ufo_enable_ttai_with_3p')) {
        sinkInteractionExtraMetrics(awc, createInteractionExtraMetricsPayloadPackage.default);
      }
      if (config !== null && config !== void 0 && (_config$extraSearchPa2 = config.extraSearchPageInteraction) !== null && _config$extraSearchPa2 !== void 0 && _config$extraSearchPa2.enabled) {
        sinkExtraSearchPageInteraction(awc, payloadPackage);
      }
    }
  });
}